import { type TextToSpeechSettings, type TextToSpeechTrack, TrackPlayerState } from '../../types/tts';
import { DeferredPromise } from '../../utils/DeferredPromise';
import { isDesktopApp, isDocumentShareApp, isExtension, isTest } from '../../utils/environment';
import ExceptionHandler from '../../utils/exceptionHandler.platform';
import findLastIndex from '../../utils/findLastIndex';
import makeLogger from '../../utils/makeLogger';
import { authenticateUriWithOneTimeToken } from '../auth.desktop';
import { CancelStateUpdate, globalState, updateState } from '../models';
import { createToast } from '../toasts.platform';
import { fetchWordBoundaries } from '../utils/fetchWordBoundaries';
import { textToSpeechDefaultPlaybackRate, textToSpeechDefaultVolume } from '../utils/tts';
import AbstractTtsController from './AbstractTtsController';

const logger = makeLogger(__filename);

class WebTtsController extends AbstractTtsController {
  trackPlayerCreationPromise = new DeferredPromise<void>();

  queue: TextToSpeechTrack[] = [];
  activeTrackIndex = 0;
  _audioElement: HTMLAudioElement;
  _isBetweenLoadStartAndLoadedDataEvents = false;
  _pendingPlayPromise: Promise<void> | null = null;


  constructor() {
    super();

    this._audioElement = document.createElement('audio');
    if (isTest) {
      return;
    }

    this._audioElement.id = 'tts-player';
    this._audioElement.controls = false;
    this._audioElement.preload = 'none';
    this._applySettingsToTrackPlayer();

    this._audioElement.addEventListener('loadstart', () => {
      this._isBetweenLoadStartAndLoadedDataEvents = true;
    });
    this._audioElement.addEventListener('loadeddata', () => {
      this._isBetweenLoadStartAndLoadedDataEvents = false;
    });
    this._audioElement.addEventListener('play', fetchWordBoundaries);
    this._syncSettingsToTrackPlayer();

    document.body.appendChild(this._audioElement);

    logger.debug('audio element created');
    this.trackPlayerCreationPromise.resolve();
  }

  async updateCurrentPlayingTrackInState() {
    const currentTrack = this.queue[this.activeTrackIndex];
    await updateState(
      (state) => {
        const tts = state.tts;
        if (!tts || tts.playingDocId === currentTrack.docId && tts.playingChunkId === currentTrack.chunkId) {
          throw new CancelStateUpdate();
        }
        state.tts = { ...tts, playingChunkId: currentTrack.chunkId, playingDocId: currentTrack.docId };
        state.tts.trackPlayerInfo = { isPlaying: false, ...state.tts.trackPlayerInfo ?? {}, position: null };
      },
      {
        eventName: 'currently-playing-tts-track-updated',
        userInteraction: null,
      },
    );
  }

  async _skipToTrackByChunkId(chunkId: string) {
    const index = findLastIndex(this.queue, (track: TextToSpeechTrack) => track.chunkId === chunkId);
    return this._skipToTrackByIndex(index);
  }

  async _getCurrentTrackIndex() {
    return this.activeTrackIndex;
  }

  async _getCurrentTrackLength() {
    return this._audioElement.duration;
  }

  async _setQueue(tracks: TextToSpeechTrack[]) {
    logger.debug('_setQueue', { tracks });
    await this._pendingPlayPromise;
    if (tracks.length === 0) {
      this.queue = [];
      this.activeTrackIndex = -1;
      return;
    }
    this.queue = tracks;
  }

  async _getAllActiveTracks() {
    return this.queue;
  }

  async _setAudioSrcToActiveTrackIndex(url: string) {
    let audioSrcUri: string;
    if (isDesktopApp) {
      // desktop app doesn't send .readwise.io cookies since it runs on tauri://localhost, so need to
      // manually request a one-time authentication token and add it to the URL query params.
      audioSrcUri = await authenticateUriWithOneTimeToken(url);
    } else {
      audioSrcUri = url;
    }
    this._audioElement.src = audioSrcUri;
    this._applySettingsToTrackPlayer();
  }

  async _skipToTrackByIndex(index: number) {
    if (index < 0 || index > this.queue.length - 1) {
      return;
    }
    await this.pause();
    this.activeTrackIndex = index;
    const track = this.queue[this.activeTrackIndex];
    await this.updateCurrentPlayingTrackInState();
    await this._setAudioSrcToActiveTrackIndex(track.url);
    await this.play();
  }


  _applySettingsToTrackPlayer(settingsArgument?: TextToSpeechSettings) {
    const ttsSettings = settingsArgument ?? this._getSettings();
    logger.debug('_applySettingsToTrackPlayer', ttsSettings);
    this._audioElement.playbackRate = ttsSettings?.playbackRate ?? textToSpeechDefaultPlaybackRate;
    this._audioElement.volume = ttsSettings?.volume ?? textToSpeechDefaultVolume;
  }

  async _getTrackPlayerPosition() {
    logger.debug('_getTrackPlayerPosition');
    return this._audioElement.currentTime;
  }

  async _getTrackPlayerState() {
    let state: TrackPlayerState;
    if (!this._audioElement.src) {
      state = TrackPlayerState.Off;
    } else if (this._audioElement.paused) {
      state = TrackPlayerState.Paused;
    } else if (this._isBetweenLoadStartAndLoadedDataEvents) {
      state = TrackPlayerState.Loading;
    } else {
      state = TrackPlayerState.Playing;
    }
    logger.debug('_getTrackPlayerState', { state });
    return state;
  }

  async _pauseTrackPlayer() {
    logger.debug('_pauseTrackPlayer');
    await this._pendingPlayPromise;
    this._audioElement.pause();
  }

  async _resetTrackPlayer() {
    logger.debug('_resetTrackPlayer');
    await this._pendingPlayPromise;
    await this._pauseTrackPlayer();
    this._audioElement.src = '';
    this._audioElement.currentTime = 0;
    this._isBetweenLoadStartAndLoadedDataEvents = false;
    this._applySettingsToTrackPlayer();
  }

  async _seekTrackPlayerWithinChunk(position: number) {
    logger.debug('_seekTrackPlayer', { position });
    this._audioElement.currentTime = position;
  }

  async _startPlayingTrackPlayer() {
    logger.debug('_startPlayingTrackPlayer');
    this._pendingPlayPromise ??= this._audioElement
      .play()
      .then(() => {
        this._pendingPlayPromise = null;
      })
      .catch((e) => {
        const state = globalState.getState();
        ExceptionHandler.captureException(e, {
          extra: {
            audioElement: this._audioElement,
            tts: state.tts,
            openDocID: state.openDocumentId,
          },
        });
        this._pendingPlayPromise = null;
        this._setIsPlaying(false);
        updateState(
          (state) => {
            state.tts = null;
          },
          {
            eventName: 'tts-start-play-error',
            userInteraction: null,
          },
        );
        createToast({ category: 'error', content: 'Text-To-Speech Error' });
      });
    return this._pendingPlayPromise;
  }

  _syncSettingsToTrackPlayer() {
    globalState.subscribe(
      (fullState) => fullState.persistent.settings.tts_v2,
      (ttsSettings) => this._applySettingsToTrackPlayer(ttsSettings),
    );
  }
}

const ttsController =
  isDocumentShareApp || isExtension
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    ? ({} as WebTtsController) // claude eslint: intentional stub — TTS is completely disabled in share/extension contexts; the empty object is never actually called
    : new WebTtsController();

export default ttsController;
